import { Instance, SnapshotIn, types, flow } from 'mobx-state-tree';
import { DataState, DataStateStore } from '@ace/core';

import { useTheme } from '@mui/material/styles';
import { ThemeProps } from '@styles/theme';

import { GradingsStore } from './Gradings.store';
import { getStepDesignedNumber } from 'NeoAnalytics/executionSteps';

import { getVariationByModuleName } from 'NeoAnalytics/procedureVariations';

export const toSpaced = (s: string) => (s.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[0-9]+/g) ?? []).join(" ");

export enum SAFETY_GRADES {
  Unsafe = 0,
  NotUNSafe = 1,
  Safe = 2,
  ProactiveSafe = 3,
  EnhanceSafety = 4,
}

export const getSafetyGradeLabel = (value: SAFETY_GRADES) => {
    switch (value) {
                    case SAFETY_GRADES.NotUNSafe:
                        return 'Not UNsafe';
                    case SAFETY_GRADES.EnhanceSafety:
                        return 'Enhanced Safety';
                    default:
                        return toSpaced(SAFETY_GRADES[value]);
    }
}

export const SAFETY_GRADES_OPTIONS: Array<{
  value: SAFETY_GRADES;
  label: string;
}> = [
    SAFETY_GRADES.Unsafe,
    SAFETY_GRADES.NotUNSafe,
    SAFETY_GRADES.Safe,
    SAFETY_GRADES.ProactiveSafe,
    SAFETY_GRADES.EnhanceSafety,
].map(value => ({ value, label: getSafetyGradeLabel(value) }));

export enum GRADES {
  Insufficient = 0,
  Sufficient = 1,
  Standard = 2,
  AboveStandard = 3,
  Outstanding = 4,
}

export const GRADES_OPTIONS: Array<{
  value: GRADES;
  label: string;
}> = [
    GRADES.Insufficient,
    GRADES.Sufficient,
    GRADES.Standard,
    GRADES.AboveStandard,
    GRADES.Outstanding,
].map(value => ({ value, label: toSpaced(GRADES[value]) }));

// const theme = useTheme<ThemeProps>();
export const getGradingColor = (v: number, isText?: boolean) =>{
    const theme = useTheme<ThemeProps>();

    switch(v){
                    case 0:
                        return isText ? theme.colors.white : theme.colors.red;
                    case 1:
                        return isText ? theme.colors.white : 'orange';
                    case 2:
                        return isText ? theme.colors.black : theme.colors.grey_light;
                    case 3:
                        return isText ? theme.colors.black : 'lightgreen';
                    case 4:
                        return isText ? theme.colors.white : theme.colors.green;
                    default:
                        return;
    }
}

export type GradingNote = {
    compId: number;
    text: string;
}
export type GradingValue = {
    compId: number;
    grade: GRADES;
    safetyGrade: SAFETY_GRADES;
}
export type GradingBehaviourStep = {
    stepId: number | string;
    stepName: string;
    obDashboardName?: string;
    stepDetectionTimestampVideoStart: string,
    stepDetectionTimestampVideoEnd: string,
    obDetected: boolean;
    obAdaptedDescription?: string;
}
export type GradingBehaviour = {
    obId: string;
    obName: string;
    obDescription: string;
    obAdaptedDescription: string;
    steps: GradingBehaviourStep[];
}
export type GradingCompetency = {
    id: number;
    title: string;
    acronym?: string;
    description: string;
    behaviours: GradingBehaviour[];
}

export type GradingDraftValue = {
    safetyGrade?: SAFETY_GRADES;
    grade?: GRADES;
    notes?: string;
}

export type GradingDraftSelection = {
    compId: number;
    grade?: GRADES;
    safetyGrade?: SAFETY_GRADES;
    notes: string;
}

// Signature of the remote grading a draft was based on. Used to invalidate a
// local draft once the remote state has moved ahead (someone else graded/edited).
export type GradingDraftRemoteVersion = {
    gradingId: string;
    reviewsCount: number;
    updatedAtMs: number;
}

export type GradingEditorDraft = {
    competencies: GradingCompetency[];
    gradings: Record<number, GradingDraftValue>;
    activeCompId?: number;
    showRecap: boolean;
    // In-progress selection for the active competency that hasn't been committed
    // to `gradings` yet (user changed a value without navigating away).
    selection?: GradingDraftSelection;
    remoteVersion: GradingDraftRemoteVersion;
}

const isRemoteGradingNewer = (current: GradingDraftRemoteVersion, base?: GradingDraftRemoteVersion) => {
    if (!base) {
        return true;
    }
    if (current.gradingId !== base.gradingId) {
        return true;
    }
    if (current.reviewsCount > base.reviewsCount) {
        return true;
    }

    return current.updatedAtMs > base.updatedAtMs;
}

export type GradingStep = {
    stepId: number | string,
    stepName: string,
    stepDetectionTimestampVideoStart: string,
    stepDetectionTimestampVideoEnd: string,
    stepDetected: boolean,
    observableBehaviours: [
        {
            obId: string,
            obDescription: string,
            obDashboardName?: string,
            obAdaptedDescription: string,
            obDetected: boolean
        }
    ]
}

type NormalizedGradingBehaviour = {
    obId: string;
    obDashboardName: string;
    obDescription: string;
    obAdaptedDescription: string;
    obDetected: boolean;
};

type NormalizedGradingStep = {
    stepId: number | string;
    stepName: string;
    stepDetectionTimestampVideoStart: string;
    stepDetectionTimestampVideoEnd: string;
    observableBehaviours: NormalizedGradingBehaviour[];
};

const parseFeedbackNotes = (feedback?: string | null): GradingNote[] => {
    if (!feedback) {
        return [];
    }

    return feedback.split('||').flatMap(entry => {
        if (!entry) {
            return [];
        }

        const [header, ...rest] = entry.split(':');
        const compId = Number(header?.split(' ')[1]);
        const text = rest.join(':').trimStart();

        if (!Number.isFinite(compId)) {
            return [];
        }

        return [{ compId, text }];
    });
};

const getNormalizedProcedureSteps = (details?: {
    procedure_steps?: Array<Record<string, any>>;
    ob_data?: Array<Record<string, any>>;
} | null) => (details?.procedure_steps ?? details?.ob_data ?? []).map(normalizeGradingStep);

const normalizeObservableBehaviour = (stepName: string, observable: Record<string, any>): NormalizedGradingBehaviour => ({
    obId: observable?.obId ?? observable?.ob_id ?? '',
    obDashboardName: observable?.obDashboardName ?? observable?.ob_dashboardname ?? stepName ?? '',
    obDescription: observable?.obDescription ?? observable?.ob_description ?? '',
    obAdaptedDescription: observable?.obAdaptedDescription ?? observable?.ob_adapted_description ?? '',
    obDetected: observable?.obDetected ?? observable?.ob_detected ?? false,
});

const normalizeGradingStep = (step: Record<string, any>): NormalizedGradingStep => ({
    stepId: step?.stepId ?? step?.step_id ?? 0,
    stepName: step?.stepName ?? step?.step_name ?? '',
    stepDetectionTimestampVideoStart: step?.stepDetectionTimestampVideoStart ?? step?.step_detection_timestamp_video_start ?? '',
    stepDetectionTimestampVideoEnd: step?.stepDetectionTimestampVideoEnd ?? step?.step_detection_timestamp_video_end ?? '',
    observableBehaviours: (step?.observableBehaviours ?? step?.observable_behaviors ?? []).map((observable: Record<string, any>) => normalizeObservableBehaviour(step?.stepName ?? step?.step_name ?? '', observable)),
});

export const getNormalizedGradingValue = (
    gradingValue?: {
        compId?: number | null;
        comp_id?: number | null;
        grade?: GRADES | null;
        grading?: GRADES | null;
        safetyGrade?: SAFETY_GRADES | null;
        safety_grade?: SAFETY_GRADES | null;
    }
): GradingValue => ({
    compId: Number(gradingValue?.compId ?? gradingValue?.comp_id ?? 0),
    grade: (gradingValue?.grade ?? gradingValue?.grading ?? 2) as GRADES,
    safetyGrade: (gradingValue?.safetyGrade ?? gradingValue?.safety_grade ?? 2) as SAFETY_GRADES,
});

export const getGradingStepLabel = (step?: Partial<GradingBehaviourStep> & Record<string, any>) => (
    step?.obDashboardName ?? step?.ob_dashboardname ?? step?.stepName ?? step?.step_name ?? ''
);

type VariationLookupExecution = {
    entityName?: string | null;
    entityVersion?: string | null;
    entityId?: string | null;
    entityAdditionalData?: Record<string, unknown> | null;
    steps?: Array<{ details?: unknown }> | null;
};

const getVariationLookupString = (executionOrName: string | VariationLookupExecution) => {
    if (typeof executionOrName === 'string') {
        return executionOrName;
    }

    const sceneName = executionOrName.entityAdditionalData && typeof executionOrName.entityAdditionalData === 'object'
        ? executionOrName.entityAdditionalData.Scene
        : undefined;

    const stepNumbers = (executionOrName.steps ?? [])
        .map(step => getStepDesignedNumber({
            details: step.details && typeof step.details === 'object'
                ? step.details as Record<string, unknown>
                : null,
        }))
        .filter((value): value is string => Boolean(value));

    return [
        executionOrName.entityName,
        executionOrName.entityVersion,
        executionOrName.entityId,
        typeof sceneName === 'string' ? sceneName : undefined,
        ...stepNumbers,
    ]
        .filter((value): value is string => Boolean(value))
        .join(' ');
};

const getBehaviourTitle = ({
    obId,
    obAdaptedDescription,
    obDescription,
    fallbackDescription,
}: {
    obId: string;
    obAdaptedDescription?: string;
    obDescription?: string;
    fallbackDescription?: string;
}) => {
    const description = obAdaptedDescription || obDescription || fallbackDescription || '';

    return `${obId} ${description}`.trim();
}

const getVisibleBehaviours = (behaviours: GradingBehaviour[]) => behaviours.filter(behaviour => behaviour.steps.length > 0);

const createFormattedCompetency = (
    refComp: typeof competenciesBehavioursRef[number],
    behaviours: GradingBehaviour[]
) => {
    const sorted = behaviours.slice().sort((a, b) => {
        const ap = a.obId.split('.').map(Number);
        const bp = b.obId.split('.').map(Number);

        return (ap[0] - bp[0]) || ((ap[1] ?? 0) - (bp[1] ?? 0));
    });

    const visibleBehaviours = getVisibleBehaviours(sorted);

    if (visibleBehaviours.length === 0) {
        return null;
    }

    const formattedComp: GradingCompetency = {
        id: +refComp.id,
        title: refComp.name,
        acronym: refComp.acronym,
        description: refComp.description,
        behaviours: visibleBehaviours,
    };

    return formattedComp;
}

const sortBehaviourSteps = (steps: GradingBehaviourStep[]) => steps.sort((left, right) => {
    const leftNumber = Number(left.stepId);
    const rightNumber = Number(right.stepId);

    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return leftNumber - rightNumber;
    }

    return String(left.stepId).localeCompare(String(right.stepId), undefined, { numeric: true, sensitivity: 'base' });
});

const appendObservableStep = (
    behavioursByCompId: Record<string, { behaviours: GradingBehaviour[] }>,
    step: NormalizedGradingStep,
    observable: NormalizedGradingBehaviour,
) => {
    const compId = observable.obId.split('.')[0];
    const refComp = competenciesBehavioursRef.find(competency => competency.id == compId);
    const refOb = refComp?.behaviours.find(behaviour => behaviour.id == observable.obId);

    behavioursByCompId[compId] ??= { behaviours: [] };

    const bucket = behavioursByCompId[compId].behaviours;
    const nextStep: GradingBehaviourStep = {
        stepId: step.stepId ? step.stepId : 0,
        stepName: step.stepName,
        obDashboardName: observable.obDashboardName,
        stepDetectionTimestampVideoStart: step.stepDetectionTimestampVideoStart,
        stepDetectionTimestampVideoEnd: step.stepDetectionTimestampVideoEnd,
        obDetected: observable.obDetected,
        obAdaptedDescription: observable.obAdaptedDescription,
    };

    const existingBehaviour = bucket.find(behaviour => behaviour.obId === observable.obId);

    if (!existingBehaviour) {
        bucket.push({
            obId: observable.obId,
            obName: getBehaviourTitle({
                obId: observable.obId,
                obAdaptedDescription: observable.obAdaptedDescription,
                obDescription: observable.obDescription,
                fallbackDescription: refOb?.description,
            }),
            obDescription: observable.obDescription,
            obAdaptedDescription: observable.obAdaptedDescription,
            steps: [nextStep],
        });

        return;
    }

    // A single procedure step can hold several observations that share the same
    // ob_id but describe different things (distinct ob_dashboardname) — e.g. both
    // "Correct code entered on keypad" and "Look into camera" carry ob_id "1.2" in
    // the same step. Keying only on stepId would collapse them into one and drop
    // the rest, so the dashboard name is part of the identity.
    const existingStep = existingBehaviour.steps.find(currentStep =>
        String(currentStep.stepId) === String(nextStep.stepId)
        && (currentStep.obDashboardName ?? '') === (nextStep.obDashboardName ?? '')
    );

    if (!existingStep) {
        existingBehaviour.steps.push(nextStep);
        sortBehaviourSteps(existingBehaviour.steps);

        return;
    }

    existingStep.obDetected = existingStep.obDetected || nextStep.obDetected;
    existingStep.stepName = existingStep.stepName || nextStep.stepName;
    existingStep.obDashboardName = existingStep.obDashboardName || nextStep.obDashboardName;
    existingStep.stepDetectionTimestampVideoStart = existingStep.stepDetectionTimestampVideoStart || nextStep.stepDetectionTimestampVideoStart;
    existingStep.stepDetectionTimestampVideoEnd = existingStep.stepDetectionTimestampVideoEnd || nextStep.stepDetectionTimestampVideoEnd;
    existingStep.obAdaptedDescription = existingStep.obAdaptedDescription || nextStep.obAdaptedDescription;
};

const ensureReferenceBehaviours = (
    behavioursByCompId: Record<string, { behaviours: GradingBehaviour[] }>,
    formattedComps: GradingCompetency[],
) => {
    Object.entries(behavioursByCompId).forEach(([compId, behaviours]) => {
        const refComp = competenciesBehavioursRef.find(competency => competency.id == compId);

        if (!refComp) {
            return;
        }

        refComp.behaviours.forEach(refBehaviour => {
            behavioursByCompId[compId] ??= { behaviours: [] };

            const bucket = behavioursByCompId[compId].behaviours;
            const existingBehaviour = bucket.find(behaviour => behaviour.obId === refBehaviour.id);

            if (!existingBehaviour) {
                bucket.push({
                    obId: refBehaviour.id,
                    obName: getBehaviourTitle({
                        obId: refBehaviour.id,
                        fallbackDescription: refBehaviour.description,
                    }),
                    obDescription: refBehaviour.description,
                    obAdaptedDescription: '',
                    steps: [],
                });
            }
        });

        const formattedComp = createFormattedCompetency(refComp, behaviours.behaviours);

        if (formattedComp) {
            formattedComps.push(formattedComp);
        }
    });
};

// IMPORTANT
const competenciesBehavioursRef = [
    {
        id: '1',
        name: 'Application of policies and procedures',
        acronym: 'APP',
        description: 'Identifies and applies applicable knowledge, policies, and procedures',
        behaviours:[
            {
                id: '1.1',
                description: 'Identifies where to find policies and procedures',
            },
            {
                id: '1.2',
                description: 'Applies relevant policies and procedures',
            },
            {
                id: '1.3',
                description: 'Applies procedures or adapts them to ensure safety',
            },
            {
                id: '1.4',
                description: 'Operates cabin systems and equipment',
            },
            {
                id: '1.5',
                description: 'Complies with applicable policies and procedures',
            },
        ],
    },
    {
        id: '2',
        name: 'Communication',
        acronym: 'COM',
        description: 'Communicates through appropriate means in the operational environment, in both normal, abnormal, and emergency situations',
        behaviours:[
            {
                id: '2.1',
                description: 'Determines that the recipient is ready and able to receive information',
            },
            {
                id: '2.2',
                description: 'Selects appropriately what, when, how and with whom to communicate',
            },
            {
                id: '2.3',
                description: 'Conveys messages clearly, using designated common language (e.g. multilingual flight/cabin crew)',
            },
            {
                id: '2.4',
                description: 'Confirms that the recipient demonstrates understanding of important information',
            },
            {
                id: '2.5',
                description: 'Listens actively and demonstrates understanding when receiving information',
            },
            {
                id: '2.6',
                description: 'Asks relevant and effective questions',
            },
            {
                id: '2.7',
                description: 'Uses appropriate escalation in communication',
            },
            {
                id: '2.8',
                description: 'Uses and interprets non-verbal communication appropriately',
            },
            {
                id: '2.9',
                description: 'Adheres to standard phraseology and procedures',
            },
        ],
    },
    {
        id: '3',
        name: 'Leadership and teamwork',
        acronym: 'LTW',
        description: 'Influences others to contribute to a shared purpose and collaborates to accomplish the goals of the team',
        behaviours:[
            {
                id: '3.1',
                description: 'Encourages participation and open communication',
            },
            {
                id: '3.2',
                description: 'Demonstrates initiative and provides direction when required',
            },
            {
                id: '3.3',
                description: 'Engages others in planning',
            },
            {
                id: '3.4',
                description: 'Considers input from others',
            },
            {
                id: '3.5',
                description: 'Gives and receives feedback constructively',
            },
            {
                id: '3.6',
                description: 'Addresses and resolves conflicts and disagreements in a constructive manner',
            },
            {
                id: '3.7',
                description: 'Exercise decisive leadership when required',
            },
            {
                id: '3.8',
                description: 'Accepts responsibility for decisions and actions',
            },
            {
                id: '3.9',
                description: 'Carries out instructions when directed',
            },
            {
                id: '3.10',
                description: 'Identifies deviations and safety hazards and applies effective intervention strategies',
            },
            {
                id: '3.11',
                description: 'Manages cultural and language challenges',
            },
        ],
    },
    {
        id: '4',
        name: 'Passenger management',
        acronym: 'PSM',
        description: 'Demonstrates effective passenger management techniques',
        behaviours:[
            {
                id: '4.1',
                description: 'Exhibits assertiveness when required (e.g. ditching, evacuation, …)',
            },
            {
                id: '4.2',
                description: 'Identifies and manages conflict between passengers',
            },
            {
                id: '4.3',
                description: 'Demonstrates conflict resolution techniques',
            },
            {
                id: '4.4',
                description: 'Informs and monitors passengers for compliance with regulations',
            },
            {
                id: '4.5',
                description: 'Uses effective communication and tone of voice appropriate to the circumstances',
            },
        ],
    },
    {
        id: '5',
        name: 'Problem solving and decision making',
        acronym: 'PSD',
        description: 'Identifies precursors, mitigates problems; and makes decisions',
        behaviours:[
            {
                id: '5.1',
                description: 'Identifies, assesses and manages threats and errors in a timely manner',
            },
            {
                id: '5.2',
                description: 'Seeks accurate and adequate information from appropriate sources',
            },
            {
                id: '5.3',
                description: 'Identifies and verifies what and why things have gone wrong, if appropriate',
            },
            {
                id: '5.4',
                description: 'Perseveres in working through problems while prioritizing safety',
            },
            {
                id: '5.5',
                description: 'Identifies and considers appropriate options',
            },
            {
                id: '5.6',
                description: 'Applies appropriate and timely decision-making techniques',
            },
            {
                id: '5.7',
                description: 'Monitors, reviews and adapts decisions as required',
            },
            {
                id: '5.8',
                description: 'Adapts when faced with situations where no guidance or procedure exists',
            },
            {
                id: '5.9',
                description: 'Demonstrates resilience when encountering an unexpected event',
            },
        ],
    },
    {
        id: '6',
        name: 'Situation awareness and management of information',
        acronym: 'SAW',
        description: 'Perceives, comprehends and manages information and anticipates its effect on the operation.',
        behaviours:[
            {
                id: '6.1',
                description: 'Monitors and assesses passenger and crew behaviour',
            },
            {
                id: '6.2',
                description: 'Monitors and assesses the general environment, state of the aircraft and cabin systems as these may affect the operation',
            },
            {
                id: '6.3',
                description: 'Validates the accuracy of information and checks for errors',
            },
            {
                id: '6.4',
                description: 'Maintains awareness of the people involved in or affected by the operation and their capacity to perform as expected',
            },
            {
                id: '6.5',
                description: 'Develops effective contingency plans based upon risks associated with threats and errors',
            },
            {
                id: '6.6',
                description: 'Responds to indications of reduced personal situation awareness',
            },
        ],
    },
    {
        id: '7',
        name: 'Workload management',
        acronym: 'WLM',
        description: 'Maintains available workload capacity by prioritizing and distributing tasks using appropriate resources',
        behaviours:[
            {
                id: '7.1',
                description: 'Plans, prioritizes and monitors tasks through the utilization of all available resources',
            },
            {
                id: '7.2',
                description: 'Manages time efficiently when carrying out tasks',
            },
            {
                id: '7.3',
                description: 'Offers and gives assistance',
            },
            {
                id: '7.4',
                description: 'Delegates tasks',
            },
            {
                id: '7.5',
                description: 'Seeks and accepts assistance, when appropriate',
            },
            {
                id: '7.6',
                description: 'Monitors, reviews and cross-checks actions',
            },
            {
                id: '7.7',
                description: 'Verifies that tasks are completed to the expected outcome',
            },
            {
                id: '7.8',
                description: 'Manages and recovers from interruptions, distractions, variations and failures effectively while performing tasks',
            },
        ],
    },
]


export const GradingsUIStoreInferred = types
    .model('GradingsUIStoreInferred', {
        _grading: types.optional(GradingsStore, {}),
        gradingFound: types.optional(types.boolean, false),
        editing: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        status: DataStateStore.create({ state: DataState.initial }),
        draftsByExecutionId: {} as Record<string, GradingEditorDraft>,
    }))
    .views(self => ({
        get grading(){
            return self._grading
        },
        get hasReviewedGrading() {
            return Boolean(self._grading.isReviewed) || self._grading.reviews.length > 0;
        },
        get remoteGradingVersion(): GradingDraftRemoteVersion {
            const grading = self._grading;
            const timestamps = [grading.updatedAt, grading.createdAt, ...grading.reviews.map(review => review.timestamp)]
                .map(value => (value ? Date.parse(value) : NaN))
                .filter(value => Number.isFinite(value)) as number[];

            return {
                gradingId: grading.id,
                reviewsCount: grading.reviews.length,
                updatedAtMs: timestamps.length ? Math.max(...timestamps) : 0,
            };
        },
        get groupedBehaviours(){
            const grading = self._grading;

            const formattedComps: GradingCompetency[] = [];
            const gradingValues: GradingValue[] = [];
            const notes: GradingNote[] = [];

            if((Boolean(grading.isReviewed) || grading.reviews.length > 0) && grading.reviews.length > 0){

                const lastReview = grading.reviews.reduce(
                    (a, b) => (Date.parse(b.timestamp) > Date.parse(a.timestamp) ? b : a),
                    grading.reviews[0]
                );

                const reviewDetails = lastReview.details;
                const gradingDetails = grading.details;
                const reviewProcedureSteps = getNormalizedProcedureSteps(reviewDetails);
                const gradingProcedureSteps = getNormalizedProcedureSteps(gradingDetails);
                const obData = reviewProcedureSteps.length > 0 ? reviewProcedureSteps : gradingProcedureSteps;
                const gradingValuesSource = (reviewDetails?.grading_values?.length ?? 0) > 0
                    ? reviewDetails?.grading_values
                    : gradingDetails?.grading_values ?? [];

                gradingValuesSource.forEach(gv => {
                    const newGrading = getNormalizedGradingValue(gv);
                    gradingValues.push(newGrading);
                });

                parseFeedbackNotes(lastReview.feedback).forEach(note => {
                    notes.push(note);
                });

                if (notes.length === 0) {
                    parseFeedbackNotes(grading.feedback).forEach(note => {
                        notes.push(note);
                    });
                }

                const observableBheaviours: Record<string, {behaviours: GradingBehaviour[]}> = {};

                obData.forEach(s =>{
                    s.observableBehaviours.forEach(o =>{
                        appendObservableStep(observableBheaviours, s, o);
                    });
                })

                ensureReferenceBehaviours(observableBheaviours, formattedComps);

                return {formattedComps, gradingValues, notes};
            }else if(grading.details){
                const obData = getNormalizedProcedureSteps(grading.details);

                grading.details.grading_values.forEach(gv => {
                    const newGrading = getNormalizedGradingValue(gv);
                    gradingValues.push(newGrading);
                });

                parseFeedbackNotes(grading.feedback).forEach(note => {
                    notes.push(note);
                });

                const observableBheaviours: Record<string, {behaviours: GradingBehaviour[]}> = {};

                obData.forEach(s =>{
                    s.observableBehaviours.map(o =>{
                        appendObservableStep(observableBheaviours, s, o);
                    });
                })

                ensureReferenceBehaviours(observableBheaviours, formattedComps);

                return {formattedComps, gradingValues, notes};
            }else{

                return {formattedComps, gradingValues, notes};
            }
        },

        loadOBsVariation(executionOrName: string | VariationLookupExecution){

            const variation = getVariationByModuleName(getVariationLookupString(executionOrName));

            const formattedComps: GradingCompetency[] = [];

            if(variation){
                const obData = (variation?.procedure_steps ?? variation?.ob_data ?? []).map(normalizeGradingStep);

                const observableBheaviours: Record<string, {behaviours: GradingBehaviour[]}> = {};

                obData.forEach((s: NormalizedGradingStep) =>{
                    s.observableBehaviours.map((o: NormalizedGradingBehaviour) =>{
                        appendObservableStep(observableBheaviours, {
                            ...s,
                            stepDetectionTimestampVideoStart: s.stepDetectionTimestampVideoStart ? s.stepDetectionTimestampVideoStart : '',
                            stepDetectionTimestampVideoEnd: s.stepDetectionTimestampVideoEnd ? s.stepDetectionTimestampVideoEnd : '',
                        }, {
                            ...o,
                            obDetected: false,
                        });
                    });
                })

                ensureReferenceBehaviours(observableBheaviours, formattedComps);
            }

            return formattedComps;
        },
        get getGradingUIData(){
            return self._grading.getGradingData;
        },
    }))
    .actions(self => {

        const loadGradings = flow(function* (payload: any) {
            self.status.setLoading();
            const result = yield self._grading.loadGradings(payload);
            self.gradingFound = result.length > 0;
            self.status.setDone();

            return result;
        });

        const postGrading = flow(function* (payload: any){
            return yield self._grading.postGrading(payload);
        });

        const postReview = flow(function* (payload: any){
            return yield self._grading.postReview({gradingId: self._grading.id, reviewData: payload});
        })

        const toggleEdit = (edit: boolean) => {
            self.editing = edit;
        }

        const toggleGradingFound = (edit: boolean) => {
            self.gradingFound = edit;
        }

        const resetCurrentGrading = () => {
            self._grading.clearData();
            self.gradingFound = false;
            self.editing = false;
        }

        const saveEditorDraft = (executionId: string, draft: GradingEditorDraft) => {
            self.draftsByExecutionId[executionId] = draft;
        }

        const getEditorDraft = (executionId: string) => {
            const draft = self.draftsByExecutionId[executionId] ?? null;

            if (!draft) {
                return null;
            }

            // Invalidate the draft if the remote grading currently loaded for this
            // execution has moved ahead of the snapshot the draft was based on.
            const isForThisExecution = !self._grading.executionId || self._grading.executionId === executionId;

            if (isForThisExecution && isRemoteGradingNewer(self.remoteGradingVersion, draft.remoteVersion)) {
                delete self.draftsByExecutionId[executionId];

                return null;
            }

            return draft;
        }

        const clearEditorDraft = (executionId: string) => {
            delete self.draftsByExecutionId[executionId];
        }

        const clearData = () =>{
            self._grading.clearData();
            self.gradingFound = false;
            self.editing = false;
            self.draftsByExecutionId = {};
        }

        return {
            loadGradings,
            postGrading,
            postReview,
            toggleEdit,
            toggleGradingFound,
            resetCurrentGrading,
            saveEditorDraft,
            getEditorDraft,
            clearEditorDraft,
            clearData,
        }
    })

type GradingsUIStoreFactoryType = typeof GradingsUIStoreInferred;
interface IGradingsUIStoreFactory extends GradingsUIStoreFactoryType { }
export const GradingsUIStore: IGradingsUIStoreFactory = GradingsUIStoreInferred;
export interface IGradingsUIStore extends Instance<IGradingsUIStoreFactory> { }
export interface IGradingsUIStoreSnapshotIn extends SnapshotIn<IGradingsUIStore> { }
